/* eslint-disable no-nested-ternary */
import React, {Fragment, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import {TypeReasonCode} from 'pages/ProductsPages/constant'
import {BULK_PAYMENT_COLUMN} from 'pages/ProductUsageHistory/constant'
import useProductUsageHistory from 'features/productUsageHistory/useProductUsageHistory'
import useProductInstitutionOperationHistory from 'features/productInstitutionOperationHistory/useProductInstitutionOperationHistory'
import useProductUsageHistoryStyles from 'pages/ProductUsageHistory/style'
import {ListLicenses} from 'api/productApi'
import useProductManagement from 'features/productManagement/useProductManagement'
import {formatDate, makeDateAndTimeFormat} from 'helpers/dateHelper'
import getMonthsInRange, {MonthRange} from 'pages/ProductUsageHistory/util'
import DialogTableLogs from './DialogTableLogs'

type RechargeableMonthlyType = {
  isAdmin?: boolean
  item: ListLicenses | null
}
export type IBulkList = ListLicenses['items'][number] & {
  period: string
  AnalysisIndicators: string
  use: number
  month: {
    monthName: string
    startDate: number
    endDate: number
  }
  remark: string
}
const ManualPaymentProduct = ({
  isAdmin = false,
  item,
}: RechargeableMonthlyType) => {
  const classes = useProductUsageHistoryStyles()
  const {t, i18n} = useTranslation()
  const [showLogs, setShowLogs] = useState<boolean>(false)
  const [showAll, setShowAll] = useState<boolean>(false)
  const [selectedPackageId, setSelectedPackageId] = useState<string>('')
  const [selectedMonth, setSelectedMonth] = useState<MonthRange | null>(null)
  const {listAllPackage, getReasonCodeLabel} = useProductManagement()
  const {listLicenseLogs: listUsage} = useProductUsageHistory()
  const {listLicenseLogs: listInstitution} =
    useProductInstitutionOperationHistory()
  const listLicenseLogs = useMemo(() => {
    return isAdmin ? listInstitution : listUsage
  }, [isAdmin, listInstitution, listUsage])

  const successfulOfferLogs = (listLicenseLogs?.offerLogs ?? []).filter(
    (offer) => offer.method !== 'AUTO',
  )

  const successfulUsageLogs = (listLicenseLogs?.usageLogs ?? []).filter(
    (usage) => usage.analyzeStatus === 'COMPLETED',
  )

  const getInfoPackage = (packageId: string) => {
    let name = ''
    const item = listAllPackage?.find((pkg) => pkg.itemId === packageId)
    if (item) {
      name =
        item.displayInfos.find(
          (it) => it.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
        )?.name || ''
    }
    return name
  }

  const getCLS = (packageId: string) => {
    const item = listAllPackage?.find((pkg) => pkg.itemId === packageId)
    if (item) return item.cls || ''
    return ''
  }

  const getMethodLabel = (method: string) => {
    switch (method) {
      case 'PAYMENT_GOODS':
        return t('IPurchasePackage')
      case 'PAYMENT_CHARGE':
        return t('IChargeV1')
      case 'MANUAL':
        return t('IManualPayment')
      default:
        return method
    }
  }

  const buildDetailRows = () => {
    if (!selectedPackageId || !selectedMonth || !item) return []
    let isFirstPurchase = false
    const licenseIds = new Set(
      item?.items
        .filter((l) => l.packageItemId === selectedPackageId)
        .map((l) => l.id),
    )

    const relevantOffers = successfulOfferLogs.filter(
      (o) =>
        licenseIds.has(o.licenseItemId) &&
        o.offerDateTime >= selectedMonth.startDate &&
        o.offerDateTime <= selectedMonth.endDate,
    )

    const relevantUsages = successfulUsageLogs.filter(
      (u) =>
        licenseIds.has(u.licenseItemId) &&
        u.usageDateTime >= selectedMonth.startDate &&
        u.usageDateTime <= selectedMonth.endDate,
    )

    const offerRows = relevantOffers
      .sort((a, b) => a.offerDateTime - b.offerDateTime)
      .map((o) => {
        if (o.method === 'PAYMENT_GOODS') {
          if (!isFirstPurchase) {
            isFirstPurchase = true
          } else {
            return null
          }
        }

        return {
          number: 0,
          usageDateTime: o.offerDateTime,
          method: getMethodLabel(o.method),
          usageId: '-',
          reason: isAdmin ? o.reason : getReasonCodeLabel(o.reasonCode),
        }
      })
      .filter((row) => row !== null)

    const usageRows = relevantUsages
      .sort((a, b) => a.usageDateTime - b.usageDateTime)
      .map((u) => ({
        number: 0,
        usageDateTime: u.usageDateTime,
        method: t('IUse'),
        usageId: u.usageId,
        reason: '-',
      }))

    return [...offerRows, ...usageRows]
      .sort((a, b) => b.usageDateTime - a.usageDateTime)
      .map((row, i) => ({...row, number: i + 1}))
  }

  const buildTableRows = (licenses: any[]) => {
    const byPackage = licenses.reduce<Map<string, any[]>>((acc, lic) => {
      acc.set(lic.packageItemId, [...(acc.get(lic.packageItemId) ?? []), lic])
      return acc
    }, new Map())
    const allRows = [...byPackage.entries()].reduce<any[]>(
      (rows, [packageId, packageLicenses]) => {
        const monthMap = packageLicenses.reduce<
          Map<string, {month: MonthRange; licenseIds: Set<string>}>
        >((acc, lic) => {
          const months = getMonthsInRange(
            lic.activationDateTime,
            lic.deactivationDateTime,
          )
          return months.reduce((innerAcc, m) => {
            const year = new Date(m.startDate).getFullYear()
            const key = `${year}-${String(m.monthNumber).padStart(2, '0')}`
            const existing = innerAcc.get(key)
            if (existing) {
              existing.licenseIds.add(lic.id)
            } else {
              innerAcc.set(key, {month: m, licenseIds: new Set([lic.id])})
            }
            return innerAcc
          }, acc)
        }, new Map())
        const sortedKeys = [...monthMap.keys()].sort()
        const {packageRows} = sortedKeys.reduce<{
          packageRows: any[]
        }>(
          ({packageRows}, key) => {
            const {month, licenseIds} = monthMap.get(key)!

            const relevantUsages = successfulUsageLogs.filter(
              (u) =>
                licenseIds.has(u.licenseItemId) &&
                u.usageDateTime >= month.startDate &&
                u.usageDateTime <= month.endDate,
            )

            const use = relevantUsages.reduce(
              (sum, u) => sum + u.usageQuantity,
              0,
            )

            return {
              packageRows: [
                ...packageRows,
                {
                  id: packageId,
                  month,
                  cls: getCLS(packageId),
                  period: `${formatDate(
                    new Date(Number(item?.activationDateTime)) >=
                      new Date(month.startDate)
                      ? new Date(Number(item?.activationDateTime))
                      : new Date(month.startDate),
                  )} ~ ${formatDate(
                    new Date(month.endDate) <=
                      new Date(Number(item?.deactivationDateTime))
                      ? new Date() <= new Date(month.endDate)
                        ? new Date()
                        : new Date(month.endDate)
                      : new Date(Number(item?.deactivationDateTime)),
                  )}`,
                  AnalysisIndicators: getInfoPackage(packageId),
                  use,
                  reason: '',
                },
              ],
            }
          },
          {packageRows: []},
        )

        return [...rows, ...packageRows]
      },
      [],
    )
    const grouped = allRows.reduce<Map<string, any[]>>((acc, row) => {
      acc.set(row.period, [...(acc.get(row.period) ?? []), row])
      return acc
    }, new Map())
    return [...grouped.entries()]
      .reverse()
      .map(([, rows]) =>
        rows.toSorted(
          (a, b) => a.cls.localeCompare(b.cls) || b.packageId - a.packageId,
        ),
      )
  }

  const getListTableBulk = useMemo(() => {
    if (!item) return []
    return buildTableRows(item?.items || [])
  }, [item, listLicenseLogs])

  const rowSpanMap = getListTableBulk[0]?.reduce<Record<string, number>>(
    (acc, curr) => {
      acc[curr.period] = (acc[curr.period] || 0) + 1
      return acc
    },
    {},
  )
  const rowSpanMapGroups = getListTableBulk.slice(1).map((rows) => {
    return rows?.reduce<Record<string, number>>((acc, curr) => {
      acc[curr.period] = (acc[curr.period] || 0) + 1
      return acc
    }, {})
  })

  const getRowSpanCls = (
    item: {
      id: string
      month: {
        monthNumber: number
        startDate: number
        endDate: number
      }
      cls: string
      period: string
      AnalysisIndicators: string
      use: number
      reason: string
    }[],
  ) => {
    return item.reduce<Record<string, number>>((acc, curr) => {
      acc[curr.cls] = (acc[curr.cls] || 0) + 1
      return acc
    }, {})
  }
  const seenCls = new Set<string>()

  useEffect(() => {
    setShowAll(false)
  }, [item])

  const renderedPeriodsFirst = new Set<string>()
  return (
    <Fragment>
      <div className={classes.btnSpec}>
        <Button
          color='primary'
          variant='contained'
          onClick={() => setShowAll(true)}
        >
          {t('IViewPreviousHistory')}
        </Button>
      </div>

      <Table size='small' className={classes.table}>
        <TableHead className={classes.tableHeader}>
          {BULK_PAYMENT_COLUMN.map((item) => (
            <TableCell
              align='center'
              padding='checkbox'
              key={item.id}
              style={{
                whiteSpace: 'pre',
                width: item.key === 'period' ? 220 : 'auto',
              }}
            >
              {t(item.label)}
            </TableCell>
          ))}
        </TableHead>
        <TableBody>
          {getListTableBulk[0]?.map((row) => (
            <TableRow key={row.id} className={classes.rowCell}>
              {!renderedPeriodsFirst.has(row.period) && (
                <TableCell align='center' rowSpan={rowSpanMap[row.period]}>
                  {row.period}
                </TableCell>
              )}
              {!seenCls.has(row.cls) && (
                <TableCell
                  align='center'
                  padding='checkbox'
                  rowSpan={getRowSpanCls(getListTableBulk[0])[row.cls]}
                  className={classes.rowCell}
                >
                  {row.cls}
                </TableCell>
              )}
              {seenCls.add(row.cls) && null}
              {renderedPeriodsFirst.add(row.period) && null}

              {BULK_PAYMENT_COLUMN.map((col) => {
                if (col.key === 'period' || col.key === 'cls') return null

                return (
                  <TableCell
                    key={col.id}
                    align='center'
                    width={col.key === 'period' ? '120px' : 'auto'}
                    onClick={() => {
                      if (col.key === 'AnalysisIndicators') {
                        setShowLogs(true)
                        setSelectedPackageId(row.id)
                        setSelectedMonth(row.month)
                      }
                    }}
                  >
                    {row[col.key]}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
          {showAll &&
            getListTableBulk.slice(1).map((rows, groupIdx) => {
              const renderedPeriodsGroup = new Set<string>()
              const seenClsGroup = new Set<string>()
              return rows.map((row) => {
                return (
                  <TableRow key={row.id} className={classes.rowCell}>
                    {!renderedPeriodsGroup.has(row.period) && (
                      <TableCell
                        align='center'
                        rowSpan={rowSpanMapGroups[groupIdx][row.period]}
                      >
                        {row.period}
                      </TableCell>
                    )}
                    {renderedPeriodsGroup.add(row.period) && null}
                    {!seenClsGroup.has(row.cls) && (
                      <TableCell
                        align='center'
                        padding='checkbox'
                        rowSpan={getRowSpanCls(rows)[row.cls]}
                        className={classes.rowCell}
                      >
                        {row.cls}
                      </TableCell>
                    )}
                    {seenClsGroup.add(row.cls) && null}

                    {BULK_PAYMENT_COLUMN.map((col) => {
                      if (col.key === 'period' || col.key === 'cls') return null

                      return (
                        <TableCell
                          key={col.id}
                          align='center'
                          onClick={() => {
                            if (col.key === 'AnalysisIndicators') {
                              setShowLogs(true)
                              setSelectedPackageId(row.id)
                              setSelectedMonth(row.month)
                            }
                          }}
                        >
                          {row[col.key]}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })
            })}
        </TableBody>
      </Table>
      <DialogTableLogs
        open={showLogs}
        dataTable={buildDetailRows()}
        onClose={() => setShowLogs(false)}
        type='bulk'
      />
    </Fragment>
  )
}

export default ManualPaymentProduct
